import React from 'react';
import { Send } from 'lucide-react';
import type { UnbookedSentReportRow } from '../../services/unbookedReportsLogic';
import { formatUnbookedDay, unbookedPriority } from '../../services/unbookedReportsLogic';
import { EmptyState, LinkButton, Panel, PanelHeader, SkeletonRows } from './dashboardUi';
import type { DashboardProject } from './dashboardModel';

interface Props {
    rows: ReadonlyArray<UnbookedSentReportRow>;
    loading: boolean;
    /** Projektzeile je Kennung — die Zeile oeffnet das Projekt daraus. */
    projectsById: ReadonlyMap<string, DashboardProject>;
    onOpen: (project: DashboardProject) => void;
    limit?: number;
    ref?: React.Ref<HTMLDivElement>;
}

/**
 * Versendete Berichte OHNE Leistungsbuchung (Sicht v_unbooked_sent_reports,
 * MIGRATIONS.md Nr. 45) — nur die eigenen Projekte. Buchen passiert im
 * Projekt; die Zeile verschwindet beim naechsten Laden von selbst.
 */
export const UnbookedPanel: React.FC<Props> = ({ rows, loading, projectsById, onOpen, limit = 5, ref }) => {
    const shown = rows.slice(0, limit);
    const rest = rows.length - shown.length;
    const [expanded, setExpanded] = React.useState(false);
    const list = expanded ? rows : shown;
    return (
        <Panel ref={ref} className="scroll-mt-3">
            <PanelHeader icon={<Send />} title="Versendet, nicht gebucht" count={loading && rows.length === 0 ? null : rows.length} countTone="amber" sub={rows.length > 0 ? 'Bericht beim Kunden, Leistung fehlt' : undefined} />
            {loading && rows.length === 0 ? (
                <SkeletonRows rows={2} />
            ) : rows.length === 0 ? (
                <EmptyState tone="ok" title="Alles gebucht" hint="Jeder versendete Bericht hat seine Leistungsbuchung." />
            ) : (
                <ul className="divide-y divide-slate-100">
                    {list.map(r => {
                        const p = projectsById.get(r.project_id);
                        const urgent = unbookedPriority(r) === 'urgent';
                        const days = r.days_since_send ?? 0;
                        return (
                            <li key={r.report_history_id}>
                                <button
                                    type="button"
                                    onClick={() => p && onOpen(p)}
                                    disabled={!p}
                                    title={p ? 'Projekt öffnen' : 'Projekt nicht in deiner Liste'}
                                    className="w-full text-left px-3.5 py-2 flex items-start gap-2.5 hover:bg-slate-50 active:bg-slate-100 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
                                >
                                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${urgent ? 'bg-red-500' : 'bg-amber-500'}`} aria-hidden="true" />
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-semibold text-slate-800 truncate">Bericht #{r.inspection_number}{r.nur_zusatzleistung_gebucht ? ' · nur Zusatzleistung gebucht' : ''}</span>
                                            <span className={`text-[11px] shrink-0 tabular ${urgent ? 'text-red-700 font-semibold' : 'text-amber-700'}`}>{days} {days === 1 ? 'Tag' : 'Tage'}</span>
                                        </span>
                                        <span className="block text-[11px] text-slate-500 truncate mt-0.5">
                                            {r.project_number && <span className="font-mono">{r.project_number} · </span>}{r.project_name || '—'} · versendet {formatUnbookedDay(r.first_sent_at)}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {rest > 0 && (
                <div className="px-3.5 py-2 border-t border-slate-100 text-center">
                    <LinkButton onClick={() => setExpanded(v => !v)}>{expanded ? 'Weniger anzeigen' : `${rest} weitere anzeigen`}</LinkButton>
                </div>
            )}
        </Panel>
    );
};
